import { DialogContent } from "@mui/material";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BAA_VERSION } from "@src/appV2/redesign/InHome/BaaDialog/BaaText";
import { CbhIcon, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef, useState } from "react";

import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { BAA_SIGNATURE_NAME } from "../constants";
import { BaaDialogContent } from "./Content";
import { useAddSignature } from "./useAddSignature";

interface Props {
  modalState: UseModalState;
}

export function InHomeBaaDialog(props: Props) {
  const worker = useDefinedWorker();

  const baaRef = useRef<HTMLDivElement>(null);

  const { modalState } = props;
  const {
    mutateAsync: addSignature,
    isLoading: isLoadingSign,
    isError: isErrorSign,
  } = useAddSignature();
  const { showSuccessToast, showErrorToast } = useToast();
  const [signature, setSignature] = useState("");

  return (
    <FullScreenDialog modalState={modalState}>
      <PageHeaderTopRow
        sx={{
          backgroundColor: (theme) => theme.palette.background.tertiary,
        }}
      />
      <DialogContent
        sx={{
          p: 0,
          overflowY: "auto",
        }}
      >
        <BaaDialogContent
          worker={worker}
          signature={signature}
          setSignature={setSignature}
          isLoadingSign={isLoadingSign}
          isErrorSign={isErrorSign}
          baaRef={baaRef}
        />
      </DialogContent>

      <DialogFooter
        backgroundColor={(theme) => theme.palette.background.tertiary}
        orientation="vertical"
      >
        <LoadingButton
          fullWidth
          isLoading={isLoadingSign}
          disabled={signature === ""}
          variant="contained"
          onClick={async () => {
            try {
              if (!baaRef.current) {
                return;
              }

              await addSignature({
                data: {
                  attributes: {
                    signedDocumentHTML: baaRef.current.innerHTML,
                    documentName: BAA_SIGNATURE_NAME,
                    version: BAA_VERSION,
                  },
                  type: "signature",
                },
              });
              logEvent(APP_V2_USER_EVENTS.IN_HOME_BAA_SIGNATURE_SUBMITTED, {
                geolocation: {
                  longitude: worker.geoLocation?.coordinates[0],
                  latitude: worker.geoLocation?.coordinates[1],
                },
                documentName: BAA_SIGNATURE_NAME,
                version: BAA_VERSION,
              });
              showSuccessToast("BAA signed successfully");
              modalState.closeModal();
            } catch {
              logEvent(APP_V2_USER_EVENTS.IN_HOME_BAA_SIGNATURE_FAILURE, {
                geolocation: {
                  longitude: worker.geoLocation?.coordinates[0],
                  latitude: worker.geoLocation?.coordinates[1],
                },
                documentName: BAA_SIGNATURE_NAME,
                version: BAA_VERSION,
              });
              showErrorToast("Failed to sign BAA, please try again");
            }
          }}
        >
          <CbhIcon type="checkmark" /> I Agree
        </LoadingButton>
      </DialogFooter>
    </FullScreenDialog>
  );
}
